import { makeStyles } from "tss-react/mui";
import { useDraggable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import { Bucket } from "../../MiScheduler/Bucket/Bucket";
import { IPrioritySchedulerBucketsProps } from "../PrioritySchedulerBoard.types";
import { getCardDnDId } from "../PrioritySchedulerBoard.utils";

type TrayData = NonNullable<
  NonNullable<IPrioritySchedulerBucketsProps["trays"]>[number]["data"]
>;

export function PrioritySchedulerBuckets({
  trays,
  onCardClick,
  EventComponent,
  indicatorWidth,
}: Readonly<IPrioritySchedulerBucketsProps>) {
  const { classes } = useStyles();

  return (
    <Box className={classes.bucketContainer} minWidth={indicatorWidth}>
      {trays?.map((bucket) => (
        <Bucket
          key={bucket.id}
          bucket={{
            data: bucket.data,
            ...bucket,
          }}
        >
          <BucketCards
            trayId={bucket.id}
            onCardClick={onCardClick}
            EventComponent={EventComponent}
            data={bucket.data}
          />
        </Bucket>
      ))}
    </Box>
  );
}

function BucketCards({
  data,
  trayId,
  onCardClick,
  EventComponent,
}: Readonly<{
  trayId: string;
  data?: TrayData;
  onCardClick: IPrioritySchedulerBucketsProps["onCardClick"];
  EventComponent: IPrioritySchedulerBucketsProps["EventComponent"];
}>) {
  return (
    <Box display="flex" gap={1}>
      {data?.map((event: TrayData[number]) => (
        <DraggableTrayCard
          key={event.id}
          trayId={trayId}
          event={event}
          onCardClick={onCardClick}
          EventComponent={EventComponent}
        />
      ))}
    </Box>
  );
}

function DraggableTrayCard({
  trayId,
  event,
  onCardClick,
  EventComponent,
}: Readonly<{
  trayId: string;
  event: TrayData[number];
  onCardClick: IPrioritySchedulerBucketsProps["onCardClick"];
  EventComponent: IPrioritySchedulerBucketsProps["EventComponent"];
}>) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: getCardDnDId(event.id),
    data: {
      type: "card",
      cardId: event.id,
      container: { type: "tray", id: trayId },
    },
  });

  return (
    <Box
      ref={setNodeRef}
      flexShrink={0}
      sx={{ opacity: isDragging ? 0.45 : 1 }}
      {...attributes}
      {...listeners}
    >
      <EventComponent attempt={event} onCardClick={onCardClick} />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  bucketContainer: {
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    bottom: 0,
    left: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 3,
  },
}));
